<script setup lang="ts">
/**
 * PAGE CAGNOTTE — rangée « Photos » sous la liste des dons (Karim 2026-10-13) :
 * TOUTES les photos de la cagnotte (bannière + galerie) sur une ligne qui
 * défile — doigt, pavé tactile, molette horizontale, glisser à la souris, ou
 * flèches. Un clic ouvre la visionneuse plein écran.
 *
 * Défilement FLUIDE (Karim 2026-10-13) : pas d'aimantation sur les photos
 * (elle accrochait au pavé tactile) ; au doigt, le navigateur gère l'élan ;
 * à la souris, le rail suit la main puis continue sur son élan et ralentit
 * doucement au lâcher, comme sur un téléphone.
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import PageCagnotteGalerie from "./PageCagnotteGalerie.vue";

const props = defineProps<{ images: string[]; titreStyle: string }>();
const { getImageUrl } = useRechercheHelpers();

const rail = ref<HTMLElement | null>(null);
const auDebut = ref(true);
const aLaFin = ref(false);
const galerie = ref<number | null>(null);

function majBords() {
  const el = rail.value;
  if (!el) return;
  auDebut.value = el.scrollLeft <= 4;
  aLaFin.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
}
/** Flèches : on avance d'environ une largeur visible. */
function defiler(sens: 1 | -1) {
  arreterElan();
  const el = rail.value;
  if (el) el.scrollBy({ left: sens * el.clientWidth * 0.85, behavior: "smooth" });
}

// Glisser à la SOURIS (doigt et pavé tactile défilent déjà nativement).
let depart: { x: number; scroll: number } | null = null;
/** Derniers points du glisser (position, instant) : vitesse au lâcher. */
let traces: Array<{ x: number; t: number }> = [];
let elan: number | null = null;
const glisse = ref(false);

function arreterElan() {
  if (elan !== null) cancelAnimationFrame(elan);
  elan = null;
}
/** Après le lâcher : le rail continue sur sa lancée puis freine (friction). */
function lancerElan(vitesse: number) {
  let v = vitesse; // px/ms, sens du défilement
  let avant = performance.now();
  const pas = (maintenant: number) => {
    const el = rail.value;
    const dt = Math.min(maintenant - avant, 40);
    avant = maintenant;
    if (!el) return arreterElan();
    el.scrollLeft += v * dt;
    v *= Math.pow(0.95, dt / 16);
    const bloque = el.scrollLeft <= 0 || el.scrollLeft + el.clientWidth >= el.scrollWidth;
    elan = Math.abs(v) > 0.02 && !bloque ? requestAnimationFrame(pas) : null;
  };
  elan = requestAnimationFrame(pas);
}

function debut(e: PointerEvent) {
  if (e.pointerType !== "mouse" || !rail.value) return;
  arreterElan();
  depart = { x: e.clientX, scroll: rail.value.scrollLeft };
  traces = [{ x: e.clientX, t: performance.now() }];
  glisse.value = false;
}
function pendant(e: PointerEvent) {
  if (!depart || !rail.value) return;
  const dx = e.clientX - depart.x;
  if (!glisse.value && Math.abs(dx) > 5) {
    glisse.value = true;
    rail.value.setPointerCapture(e.pointerId);
  }
  if (!glisse.value) return;
  rail.value.scrollLeft = depart.scroll - dx;
  const t = performance.now();
  traces.push({ x: e.clientX, t });
  traces = traces.filter((p) => t - p.t < 100);
}
function fin() {
  if (depart && glisse.value && traces.length > 1) {
    const premier = traces[0]!;
    const dernier = traces[traces.length - 1]!;
    const duree = performance.now() - premier.t;
    // Main arrêtée avant de lâcher (> 100 ms) : pas d'élan.
    if (duree > 0 && duree < 120) lancerElan(-(dernier.x - premier.x) / duree);
  }
  depart = null;
  traces = [];
  // Le clic qui suit un glisser ne doit pas ouvrir la photo.
  setTimeout(() => (glisse.value = false), 0);
}
function ouvrir(i: number) {
  if (!glisse.value) galerie.value = i;
}

let observateur: ResizeObserver | null = null;
onMounted(() => {
  majBords();
  if (rail.value) {
    observateur = new ResizeObserver(majBords);
    observateur.observe(rail.value);
  }
});
watch(() => props.images, () => nextTick(majBords));
onBeforeUnmount(() => {
  observateur?.disconnect();
  arreterElan();
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between gap-4">
      <h2 class="text-2xl text-(--color-ink)" :style="titreStyle">
        {{ $t("campagne.pageCagnotte.sectionPhotos") }} <span class="text-gray-400" style="font-weight: 500 !important">{{ images.length }}</span>
      </h2>
      <div v-if="!(auDebut && aLaFin)" class="hidden sm:flex items-center gap-1.5">
        <button
          type="button"
          class="w-9 h-9 rounded-full ring-1 ring-gray-200 bg-white text-(--color-ink) flex items-center justify-center hover:ring-(--color-ink) transition-all disabled:opacity-35 disabled:hover:ring-gray-200"
          :aria-label="$t('campagne.pageCagnotte.photosPrecedentes')"
          :disabled="auDebut"
          @click="defiler(-1)"
        >
          <UIcon name="i-lucide-chevron-left" class="size-4" />
        </button>
        <button
          type="button"
          class="w-9 h-9 rounded-full ring-1 ring-gray-200 bg-white text-(--color-ink) flex items-center justify-center hover:ring-(--color-ink) transition-all disabled:opacity-35 disabled:hover:ring-gray-200"
          :aria-label="$t('campagne.pageCagnotte.photosSuivantes')"
          :disabled="aLaFin"
          @click="defiler(1)"
        >
          <UIcon name="i-lucide-chevron-right" class="size-4" />
        </button>
      </div>
    </div>

    <!-- Rail : défilement natif aimanté, barre de défilement masquée -->
    <div
      ref="rail"
      class="mt-6 flex gap-3 overflow-x-auto overscroll-x-contain select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      :class="glisse ? 'cursor-grabbing' : 'cursor-grab'"
      @scroll.passive="majBords"
      @pointerdown="debut"
      @pointermove="pendant"
      @pointerup="fin"
      @pointercancel="fin"
      @wheel.passive="arreterElan"
    >
      <button
        v-for="(id, i) in images"
        :key="id"
        type="button"
        class="group shrink-0 w-[220px] sm:w-[260px] aspect-square rounded-2xl overflow-hidden bg-[#eef2ff]"
        :aria-label="$t('campagne.pageCagnotte.voirPhoto', { n: i + 1, total: images.length })"
        @click="ouvrir(i)"
      >
        <img
          :src="getImageUrl(id, undefined, 600)"
          alt=""
          loading="lazy"
          draggable="false"
          class="w-full h-full object-cover transition-[scale] duration-500 ease-out group-hover:scale-105"
        />
      </button>
    </div>

    <PageCagnotteGalerie v-if="galerie !== null" :images="images" :depart="galerie" @fermer="galerie = null" />
  </div>
</template>
